// src/features/wf2/popup/W2NovaProgramar.jsx
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import api from "../../../api/axios";
import s from "./W2NovaProgramar.module.css";
import { novaProgramacaoW2 } from "../api";
import W2DateErrorModal from "./W2DateErrorModal.jsx";

/* Bases do WF-2 */
import {
  getGerencias,
  getCoordenadores,
  getSubs,
  getSbs,
  validarKm,
  sbKm,
  getAtivosNomes,
  getAtividadesNomes,
} from "../../execucao/api";

/* ----------------- helpers ----------------- */
const norm = (v) => (v == null ? "" : String(v));
const trim = (v) => norm(v).trim();
const up = (v) => trim(v).toUpperCase();
const keyify = (x) => String(x || "").trim().toUpperCase();
const foldSearch = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase();

const pickArray = (resp) =>
  Array.isArray(resp)
    ? resp
    : Array.isArray(resp?.data)
    ? resp.data
    : Array.isArray(resp?.data?.data)
    ? resp.data.data
    : [];

const normalizeSbItem = (x) =>
  x && typeof x === "object"
    ? String(x.sb ?? x.patio ?? x.nome ?? x.label ?? "").trim()
    : String(x ?? "").trim();

const uniqSorted = (arr) =>
  Array.from(new Set((arr || []).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b, "pt-BR")
  );

const NOVA_TZ = "America/Sao_Paulo";
const NOVA_DATE_WEEKDAYS = ["D", "S", "T", "Q", "Q", "S", "S"];

const getTodayYMD = () =>
  new Intl.DateTimeFormat("sv-SE", {
    timeZone: NOVA_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

const parseYMDToUtcDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || "").trim())) return null;
  const [year, month, day] = String(value).split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
};

const formatUtcDateToYMD = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatUtcDateToBR = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("pt-BR", { timeZone: NOVA_TZ }).format(date);
};

const startOfUtcMonth = (date) =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 12, 0, 0));

const addUtcDays = (date, amount) =>
  new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + amount, 12, 0, 0)
  );

const addUtcMonths = (date, amount) =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + amount, 1, 12, 0, 0));

const isSameUtcDay = (a, b) =>
  !!a &&
  !!b &&
  a.getUTCFullYear() === b.getUTCFullYear() &&
  a.getUTCMonth() === b.getUTCMonth() &&
  a.getUTCDate() === b.getUTCDate();

const cleanDec = (raw) =>
  String(raw ?? "")
    .replace(",", ".")
    .replace(/[^\d.-]/g, "");

function sortStringsPt(arr) {
  return (arr || [])
    .map((x) => String(x ?? "").trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, "pt-BR"));
}

function toHHMM(raw) {
  const sv = trim(raw);
  if (!sv) return "";
  let m = sv.match(/^(\d{1,2}):([0-5]\d)$/);
  if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  m = sv.match(/^(\d{1,2}):([0-5]\d):([0-5]\d)$/);
  if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  m = sv.match(/^(\d{1,2})([0-5]\d)$/);
  if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  m = sv.match(/(\d{1,2}):([0-5]\d)/);
  if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  return "";
}

function sanitizeQuantidade(raw) {
  const sx = trim(raw);
  if (!sx) return "";
  const m = sx.match(/^(-?\d+)([.,]\d+)?$/);
  if (!m) return sx;
  const inteiro = m[1];
  let frac = m[2] || "";
  if (frac) {
    frac = frac.replace(",", ".").replace(/0+$/, "");
    if (frac === "." || frac === "") frac = "";
  }
  return inteiro + (frac || "");
}

function cleanFieldLabel(label) {
  return String(label ?? "")
    .replace(/\s*\((?:opcional|optional)\)\s*/gi, "")
    .trim();
}

function normalizePrioridade(v) {
  const u = up(v);
  if (!u) return "";
  if (/^P[0-5]$/.test(u)) return u;
  if (u === "ALTA") return "P1";
  if (u === "MEDIA" || u === "MÉDIA") return "P3";
  if (u === "BAIXA") return "P5";
  return "";
}

/** opcional: retorna "" se vazio/inválido */
function intOrEmpty(v) {
  const t = trim(v);
  if (!t) return "";
  const n = Number(t);
  if (!Number.isFinite(n)) return "";
  const i = Math.trunc(n);
  return i < 0 ? "" : String(i);
}

function pickFirst(obj, keys) {
  for (const k of keys) {
    const v = obj?.[k];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

function initialForm() {
  return {
    data: "",

    gerencia: "",
    coordenador: "",
    atividade: "",
    ativo: "",

    quantidade: "",
    unidade_de_medida: "",

    os: "",
    observacao: "",

    sub: "",
    kmInicio: "",
    kmFinal: "",
    sbOrigem: "", // ✅ AGORA OPCIONAL
    sbDeTrabalho: "",
    sbEncerramento: "", // ✅ AGORA OPCIONAL

    nome_do_responsavel: "",
    telefone_do_responsavel: "",

    horario_do_intervalo: "",
    horario_inicio_jornada_turma: "",
    tempo_do_intervalo: "",

    // ✅ OPCIONAIS
    quantidade_maquinista: "",
    maquinista: "",
    locomotiva1: "",
    locomotiva2: "",
    tipo_acionamento: "",

    // ✅ OBRIGATÓRIOS
    tipo_programacao: "",
    prioridade: "",
    responsavel_pelo_aceite: "",

    kmValido: true,
    sbMsg: "",
  };
}

export default function W2NovaProgramar({ open = false, onClose = () => {} }) {
  const [gerencias, setGerencias] = useState([]);
  const [coordenadores, setCoordenadores] = useState([]);
  const [subs, setSubs] = useState([]);
  const [sbsAll, setSbsAll] = useState([]);
  const [sbsCoord, setSbsCoord] = useState([]);
  const [atividades, setAtividades] = useState([]);
  const [ativos, setAtivos] = useState([]);

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => initialForm());
  const [dateError, setDateError] = useState("");

  const [locks, setLocks] = useState({
    unidade_de_medida: true,
    nome_do_responsavel: true,
    telefone_do_responsavel: true,
  });

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const close = () => {
    try {
      onClose?.(false);
    } catch {
      onClose?.();
    }
  };

  /* reset quando abre */
  useEffect(() => {
    if (!open) return;
    setSaving(false);
    setForm(initialForm());
    setDateError("");
    setLocks({
      unidade_de_medida: true,
      nome_do_responsavel: true,
      telefone_do_responsavel: true,
    });
  }, [open]);

  /* ---------------- indexes + opções ordenadas ---------------- */
  const atividadeIndex = useMemo(() => {
    const map = new Map();
    (atividades || []).forEach((row) => {
      const nome = String(
        row?.atividade || row?.nome || row?.label || row || ""
      ).trim();
      if (!nome) return;
      const k = keyify(nome);
      if (!map.has(k)) map.set(k, row);
    });
    return map;
  }, [atividades]);

  const ativoIndex = useMemo(() => {
    const map = new Map();
    (ativos || []).forEach((row) => {
      const nome = String(
        row?.ativos || row?.ativo || row?.nome || row?.label || row || ""
      ).trim();
      if (!nome) return;
      const k = keyify(nome);
      if (!map.has(k)) map.set(k, row);
    });
    return map;
  }, [ativos]);

  const atividadeValida = !!(
    form.atividade && atividadeIndex.has(keyify(form.atividade))
  );
  const ativoValido = !!(form.ativo && ativoIndex.has(keyify(form.ativo)));

  const gerenciasOpts = useMemo(
    () => sortStringsPt((gerencias || []).map((g) => g?.gerencia ?? g)),
    [gerencias]
  );
  const coordenadoresOpts = useMemo(
    () => sortStringsPt((coordenadores || []).map((c) => c?.coordenador ?? c)),
    [coordenadores]
  );
  const subsOpts = useMemo(
    () =>
      sortStringsPt(
        (subs || []).map((x) => x?.sub ?? x?.subdivisao_id ?? x?.id ?? x)
      ),
    [subs]
  );
  const atividadesOpts = useMemo(
    () =>
      sortStringsPt(
        (atividades || [])
          .map((r) =>
            String(r?.atividade || r?.nome || r?.label || r || "").trim()
          )
          .filter(Boolean)
      ),
    [atividades]
  );
  const ativosOpts = useMemo(
    () =>
      sortStringsPt(
        (ativos || [])
          .map((r) =>
            String(r?.ativos || r?.ativo || r?.nome || r?.label || r || "").trim()
          )
          .filter(Boolean)
      ),
    [ativos]
  );

  const kmFilled = useMemo(
    () => !!(trim(form.sub) && trim(form.kmInicio) && trim(form.kmFinal)),
    [form.sub, form.kmInicio, form.kmFinal]
  );

  /* ---------------- carrega bases ao abrir ---------------- */
  useEffect(() => {
    if (!open) return;

    (async () => {
      try {
        setGerencias((await getGerencias()) || []);
      } catch {
        setGerencias([]);
      }

      try {
        const r = await api.get("/bases/atividades/listar", {
          params: { limit: 100000 },
        });
        setAtividades(pickArray(r));
      } catch {
        try {
          setAtividades(pickArray(await getAtividadesNomes()));
        } catch {
          setAtividades([]);
        }
      }

      try {
        const r = await api.get("/bases/ativos/listar", {
          params: { limit: 100000 },
        });
        setAtivos(pickArray(r));
      } catch {
        try {
          setAtivos(pickArray(await getAtivosNomes()));
        } catch {
          setAtivos([]);
        }
      }

      const tryUrls = [
        "/bases/sb-origem-encerramento/listar",
        "/sb-origem-encerramento/listar",
      ];
      let all = [];
      for (const url of tryUrls) {
        try {
          const resp = await api.get(url, { params: { limit: 100000 } });
          all = all.concat(pickArray(resp).map(normalizeSbItem));
        } catch {}
      }
      setSbsAll(uniqSorted(all));
    })();
  }, [open]);

  /* Gerência -> Coordenadores */
  const prevGer = useRef(form.gerencia);
  useEffect(() => {
    if (!open) return;

    const changed = prevGer.current !== form.gerencia;
    prevGer.current = form.gerencia;

    if (!form.gerencia) {
      setCoordenadores([]);
      return;
    }

    getCoordenadores(form.gerencia)
      .then((l) => setCoordenadores(l || []))
      .catch(() => setCoordenadores([]));

    if (changed) {
      setSubs([]);
      setSbsCoord([]);
      setForm((f) => ({
        ...f,
        coordenador: "",
        sub: "",
        kmInicio: "",
        kmFinal: "",
        sbDeTrabalho: "",
        kmValido: true,
        sbMsg: "",
        // ✅ não mexe em sbOrigem/sbEncerramento (opcionais)
      }));
    }
  }, [open, form.gerencia]);

  /* Coordenador -> SUBs + SBs */
  const prevCoord = useRef(form.coordenador);
  useEffect(() => {
    if (!open) return;

    const changed = prevCoord.current !== form.coordenador;
    prevCoord.current = form.coordenador;

    if (!form.coordenador) {
      setSubs([]);
      setSbsCoord([]);
      return;
    }

    Promise.all([getSubs(form.coordenador), getSbs(form.coordenador)])
      .then(([subsList, sbsList]) => {
        setSubs(subsList || []);
        setSbsCoord(uniqSorted((sbsList || []).map(normalizeSbItem)));
      })
      .catch(() => {
        setSubs([]);
        setSbsCoord([]);
      });

    if (changed) {
      setForm((f) => ({
        ...f,
        sub: "",
        kmInicio: "",
        kmFinal: "",
        sbDeTrabalho: "",
        kmValido: true,
        sbMsg: "",
      }));
    }
  }, [open, form.coordenador]);

  /* SUB + KM -> valida KM / SB (gera SB de Trabalho) */
  const prevTriad = useRef("");
  useEffect(() => {
    if (!open) return;

    const sub = form.sub;
    const ki = Number(cleanDec(form.kmInicio));
    const kf = Number(cleanDec(form.kmFinal));
    const key = [
      sub || "",
      Number.isNaN(ki) ? "" : ki,
      Number.isNaN(kf) ? "" : kf,
    ].join("|");
    if (prevTriad.current === key) return;
    prevTriad.current = key;

    if (sub && Number.isFinite(ki) && Number.isFinite(kf)) {
      Promise.all([validarKm(sub, ki, kf), sbKm(sub, ki, kf)])
        .then(([val, sbsList]) => {
          const arr = (Array.isArray(sbsList) ? sbsList : [])
            .map(normalizeSbItem)
            .filter(Boolean);
          const sbTxt = arr.length ? arr.join(", ") : "";

          setForm((f) => ({
            ...f,
            kmValido: !!val?.existe,
            // ✅ se encontrou SB, escreve; se não, deixa vazio (não mantém “antigo”)
            sbDeTrabalho: sbTxt,
            sbMsg: val?.existe
              ? arr.length
                ? ""
                : "Não há SB para o intervalo informado."
              : "KM fora da SUB.",
          }));
        })
        .catch(() =>
          setForm((f) => ({
            ...f,
            kmValido: false,
            sbDeTrabalho: "",
            sbMsg: "Erro ao buscar KM.",
          }))
        );
    } else {
      setForm((f) => ({
        ...f,
        kmValido: true,
        sbDeTrabalho: "",
        sbMsg: "",
      }));
    }
  }, [open, form.sub, form.kmInicio, form.kmFinal]);

  /* Atividade -> Unidade de Medida (TRAVADO) */
  useEffect(() => {
    if (!open) return;

    const row = atividadeIndex.get(keyify(form.atividade));
    if (!row) {
      setForm((f) => ({ ...f, unidade_de_medida: "" }));
      return;
    }

    const unidade =
      pickFirst(row, ["unidade_de_medida", "unidade", "udm", "uom", "medida"]) ||
      "";
    setForm((f) => ({ ...f, unidade_de_medida: unidade }));
    setLocks((l) => ({ ...l, unidade_de_medida: true }));
  }, [open, form.atividade, atividadeIndex]);

  /* Ativo -> Responsável + Telefone (TRAVADO) */
  useEffect(() => {
    if (!open) return;

    const row = ativoIndex.get(keyify(form.ativo));
    if (!row) {
      setForm((f) => ({
        ...f,
        nome_do_responsavel: "",
        telefone_do_responsavel: "",
      }));
      return;
    }

    const nomeResp = pickFirst(row, [
      "nome_do_responsavel",
      "responsavel",
      "responsável",
      "responsavel_pelo_ativo",
      "responsavel_ativo",
      "contato",
      "nome_responsavel",
    ]);

    const tel = pickFirst(row, [
      "telefone_do_responsavel",
      "telefone",
      "fone",
      "celular",
      "whatsapp",
      "telefone_responsavel",
    ]);

    setForm((f) => ({
      ...f,
      nome_do_responsavel: nomeResp || "",
      telefone_do_responsavel: tel || "",
    }));

    setLocks((l) => ({
      ...l,
      nome_do_responsavel: true,
      telefone_do_responsavel: true,
    }));
  }, [open, form.ativo, ativoIndex]);

  const setF = (k) => (e) => {
    if (locks[k]) return;

    const raw = e?.target?.value ?? e;
    let v = raw;

    if (
      k === "horario_do_intervalo" ||
      k === "tempo_do_intervalo" ||
      k === "horario_inicio_jornada_turma"
    ) {
      v = toHHMM(raw);
    }
    if (k === "prioridade") v = normalizePrioridade(raw) || raw;
    if (k === "quantidade") v = sanitizeQuantidade(raw);
    if (k === "quantidade_maquinista") v = intOrEmpty(raw);
    if (k === "kmInicio" || k === "kmFinal") v = cleanDec(raw);

    setForm((prev) => ({ ...prev, [k]: v }));
  };

  async function criar() {
    const prioridade = normalizePrioridade(form.prioridade);

    // ✅ obrigatórios (pátios agora NÃO são obrigatórios)
    if (!trim(form.data)) return alert("Informe a data.");
    if (!trim(form.gerencia)) return alert("Selecione a Gerência.");
    if (!trim(form.coordenador)) return alert("Selecione o Coordenador.");
    if (!atividadeValida)
      return alert("Selecione uma Atividade válida (dos combos).");
    if (!ativoValido) return alert("Selecione um Ativo válido (dos combos).");
    if (!trim(form.quantidade)) return alert("Informe a Quantidade.");
    if (!trim(form.unidade_de_medida))
      return alert("Unidade de Medida não encontrada. Verifique a Atividade.");
    if (!trim(form.sub)) return alert("Selecione a SUB.");
    if (!trim(form.kmInicio) || !trim(form.kmFinal))
      return alert("Informe KM Início e KM Fim.");
    if (!form.kmValido) return alert("KM fora da SUB. Ajuste o intervalo.");

    // ✅ SB de trabalho continua obrigatório (pela regra de KM/SUB)
    if (!trim(form.sbDeTrabalho))
      return alert("SB de Trabalho não encontrada. Ajuste KM/SUB.");

    if (!toHHMM(form.tempo_do_intervalo))
      return alert("Informe o Tempo do Intervalo (HH:MM).");
    if (!toHHMM(form.horario_do_intervalo))
      return alert("Informe o Horário do Intervalo (HH:MM).");
    if (!toHHMM(form.horario_inicio_jornada_turma))
      return alert("Informe o Início Jornada Turma (HH:MM).");
    if (!trim(form.nome_do_responsavel))
      return alert("Responsável pelo Ativo não encontrado. Verifique o Ativo.");
    if (!trim(form.telefone_do_responsavel))
      return alert(
        "Telefone do Responsável não encontrado. Verifique o Ativo."
      );
    if (!trim(form.tipo_programacao))
      return alert("Selecione o Tipo de Programação.");
    if (!prioridade) return alert("Selecione a Prioridade (P0–P5).");
    if (!trim(form.responsavel_pelo_aceite))
      return alert("Informe o Responsável pelo Aceite.");

    setSaving(true);
    try {
      const qtdMaq = intOrEmpty(form.quantidade_maquinista);
      const maq = trim(form.maquinista);
      const loco1 = trim(form.locomotiva1);
      const loco2 = trim(form.locomotiva2);
      const tipoAcio = trim(form.tipo_acionamento);
      const os = trim(form.os);
      const obs = trim(form.observacao);

      const sbOrigem = trim(form.sbOrigem);
      const sbEncerramento = trim(form.sbEncerramento);

      const payload = {
        data: trim(form.data),
        gerencia: trim(form.gerencia),
        coordenador: trim(form.coordenador),
        atividade: trim(form.atividade),
        ativo: trim(form.ativo),

        quantidade: sanitizeQuantidade(form.quantidade),
        unidade_de_medida: trim(form.unidade_de_medida),

        sub: trim(form.sub),
        kmInicio: cleanDec(form.kmInicio),
        kmFinal: cleanDec(form.kmFinal),

        // ✅ agora opcionais: só manda se tiver preenchido
        ...(sbOrigem ? { sbOrigem } : {}),
        sbDeTrabalho: trim(form.sbDeTrabalho),
        ...(sbEncerramento ? { sbEncerramento } : {}),

        tempo_do_intervalo: toHHMM(form.tempo_do_intervalo),
        horario_do_intervalo: toHHMM(form.horario_do_intervalo),
        horario_inicio_jornada_turma: toHHMM(form.horario_inicio_jornada_turma),

        nome_do_responsavel: trim(form.nome_do_responsavel),
        telefone_do_responsavel: trim(form.telefone_do_responsavel),

        tipo_programacao: trim(form.tipo_programacao),
        prioridade,
        responsavel_pelo_aceite: trim(form.responsavel_pelo_aceite),

        ...(os ? { os } : {}),
        ...(obs ? { observacao: obs } : {}),
        ...(qtdMaq ? { quantidade_maquinista: Number(qtdMaq) } : {}),
        ...(maq ? { maquinista: maq } : {}),
        ...(loco1 ? { locomotiva1: loco1 } : {}),
        ...(loco2 ? { locomotiva2: loco2 } : {}),
        ...(tipoAcio ? { tipo_acionamento: tipoAcio } : {}),
      };

      const out = await novaProgramacaoW2(payload);
      if (!out?.ok)
        throw new Error(
          out?.message || out?.error || "Falha ao criar (nova_programacao)."
        );

      close();
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Não foi possível criar.";
      if (/passad/i.test(msg)) {
        setDateError("Data não pode ser no passado. Escolha uma data futura.");
      } else {
        alert("Não foi possível criar.\n" + msg);
      }
    } finally {
      if (aliveRef.current) setSaving(false);
    }
  }

  if (!open) return null;

  return (
    <div className={s.backdrop}>
      <div
        className={`${s.modal} ${s.root}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={s.header}>
          <div className={s.title}>Nova Programação</div>
          <button className={s.close} onClick={close} aria-label="Fechar">
            ×
          </button>
        </div>

        <div className={s.content}>
          <section className={s.section}>
            <div className={s.sectionTitle}>Identificação</div>

            <div className={`${s.grid} ${s.grid3}`}>
              <DateFieldSelect
                label="Data"
                value={form.data}
                onChange={setF("data")}
                requiredHighlight={!trim(form.data)}
              />

              <FieldSelect
                label="Gerência"
                value={form.gerencia}
                onChange={setF("gerencia")}
                options={gerenciasOpts}
                requiredHighlight={!trim(form.gerencia)}
              />

              <FieldSelect
                label="Coordenador"
                value={form.coordenador}
                onChange={setF("coordenador")}
                options={coordenadoresOpts}
                requiredHighlight={!trim(form.coordenador)}
              />
            </div>

            <div className={`${s.grid} ${s.grid2}`}>
              <FieldSelect
                label="Atividade"
                value={form.atividade}
                onChange={setF("atividade")}
                options={atividadesOpts}
                searchable
                searchPlaceholder="Buscar atividade..."
                requiredHighlight={!atividadeValida}
              />

              <FieldSelect
                label="Ativo"
                value={form.ativo}
                onChange={setF("ativo")}
                options={ativosOpts}
                searchable
                searchPlaceholder="Buscar ativo..."
                requiredHighlight={!ativoValido}
              />
            </div>

            <div className={`${s.grid} ${s.grid3}`}>
              <FieldEdit
                label="Quantidade"
                value={form.quantidade}
                onChange={setF("quantidade")}
                placeholder="Ex.: 10"
                requiredHighlight={!trim(form.quantidade)}
              />

              <FieldEdit
                label="Unidade de Medida"
                value={form.unidade_de_medida}
                onChange={setF("unidade_de_medida")}
                placeholder="Vem da Atividade"
                requiredHighlight={!trim(form.unidade_de_medida)}
                disabled
              />

              <FieldEdit
                label="OS"
                value={form.os}
                onChange={setF("os")}
                placeholder="Ex.: 12345"
              />
            </div>

            <div className={s.grid}>
              <FieldEdit
                label="Observação"
                value={form.observacao}
                onChange={setF("observacao")}
                placeholder="Observações gerais"
                className={s.spanFull}
              />
            </div>
          </section>

          <section className={s.section}>
            <div className={s.sectionTitle}>Localização</div>

            <div className={`${s.grid} ${s.grid3}`}>
              <FieldSelect
                label="SUB"
                value={form.sub}
                onChange={setF("sub")}
                options={subsOpts}
                requiredHighlight={!trim(form.sub)}
              />

              <FieldEdit
                label="KM Início"
                value={form.kmInicio}
                onChange={setF("kmInicio")}
                placeholder="Ex.: 123.4"
                requiredHighlight={!trim(form.kmInicio) || !form.kmValido}
              />

              <FieldEdit
                label="KM Fim"
                value={form.kmFinal}
                onChange={setF("kmFinal")}
                placeholder="Ex.: 130.0"
                requiredHighlight={!trim(form.kmFinal) || !form.kmValido}
              />
            </div>

            {kmFilled && !form.kmValido && (
              <div className={s.warn}>⚠️ KM fora da SUB. Ajuste o intervalo.</div>
            )}

            <div className={`${s.grid} ${s.grid3}`}>
              {/* ✅ AGORA OPCIONAL */}
              <FieldSelect
                label="Pátio Origem (opcional)"
                value={form.sbOrigem}
                onChange={setF("sbOrigem")}
                options={sbsAll}
                searchable
                searchPlaceholder="Buscar pátio..."
                requiredHighlight={false}
              />

              <FieldEdit
                label="SB de Trabalho"
                value={form.sbDeTrabalho}
                onChange={setF("sbDeTrabalho")}
                placeholder="Gerado por KM/SUB"
                requiredHighlight={!trim(form.sbDeTrabalho)}
                disabled
              />

              {/* ✅ AGORA OPCIONAL */}
              <FieldSelect
                label="Pátio Encerramento (opcional)"
                value={form.sbEncerramento}
                onChange={setF("sbEncerramento")}
                options={sbsAll}
                searchable
                searchPlaceholder="Buscar pátio..."
                requiredHighlight={false}
              />
            </div>

            {!!form.sbMsg && <div className={s.helper}>{form.sbMsg}</div>}
            {!!sbsCoord?.length && (
              <div className={s.miniHint}>
                SBs do coordenador: {sbsCoord.join(", ")}
              </div>
            )}
          </section>

          <section className={s.section}>
            <div className={s.sectionTitle}>Horário</div>

            <div className={`${s.grid} ${s.grid3}`}>
              <FieldEdit
                label="Tempo do Intervalo"
                type="time"
                step={60}
                value={form.tempo_do_intervalo}
                onChange={setF("tempo_do_intervalo")}
                requiredHighlight={!toHHMM(form.tempo_do_intervalo)}
              />
              <FieldEdit
                label="Horário do Intervalo"
                type="time"
                step={60}
                value={form.horario_do_intervalo}
                onChange={setF("horario_do_intervalo")}
                requiredHighlight={!toHHMM(form.horario_do_intervalo)}
              />
              <FieldEdit
                label="Início Jornada Turma"
                type="time"
                step={60}
                value={form.horario_inicio_jornada_turma}
                onChange={setF("horario_inicio_jornada_turma")}
                requiredHighlight={!toHHMM(form.horario_inicio_jornada_turma)}
              />
            </div>
          </section>

          <section className={s.section}>
            <div className={s.sectionTitle}>Contato</div>

            <div className={`${s.grid} ${s.grid2}`}>
              <FieldEdit
                label="Responsável pelo Ativo"
                value={form.nome_do_responsavel}
                onChange={setF("nome_do_responsavel")}
                requiredHighlight={!trim(form.nome_do_responsavel)}
                disabled
              />
              <FieldEdit
                label="Telefone do Responsável"
                value={form.telefone_do_responsavel}
                onChange={setF("telefone_do_responsavel")}
                requiredHighlight={!trim(form.telefone_do_responsavel)}
                disabled
              />
            </div>
          </section>

          <section className={s.section}>
            <div className={s.sectionTitle}>Aprovação</div>

            {/* ✅ OPCIONAIS */}
            <div className={`${s.grid} ${s.grid2}`}>
              <FieldEdit
                label="Qtd. Maquinista (opcional)"
                value={form.quantidade_maquinista}
                onChange={setF("quantidade_maquinista")}
                placeholder="Ex.: 1"
              />
              <FieldSelect
                label="Maquinista (opcional)"
                value={form.maquinista}
                onChange={setF("maquinista")}
                options={["ESCALA", "FIXO", "FIXO + ESCALA"]}
              />
            </div>

            <div className={`${s.grid} ${s.grid2}`}>
              <FieldEdit
                label="Locomotiva 1 (opcional)"
                value={form.locomotiva1}
                onChange={setF("locomotiva1")}
              />
              <FieldEdit
                label="Locomotiva 2 (opcional)"
                value={form.locomotiva2}
                onChange={setF("locomotiva2")}
              />
            </div>

            <div className={`${s.grid} ${s.grid2}`}>
              <FieldSelect
                label="Tipo de Acionamento (opcional)"
                value={form.tipo_acionamento}
                onChange={setF("tipo_acionamento")}
                options={["SBR", "ACIONAMENTO", "AGUARDANDO ACIONAMENTO PCM"]}
              />
              <FieldSelect
                label="Tipo de Programação"
                value={form.tipo_programacao}
                onChange={setF("tipo_programacao")}
                options={["CONTRATO", "OPORTUNIDADE"]}
                requiredHighlight={!trim(form.tipo_programacao)}
              />
            </div>

            <div className={`${s.grid} ${s.grid2}`}>
              <FieldSelect
                label="Prioridade"
                value={normalizePrioridade(form.prioridade)}
                onChange={setF("prioridade")}
                options={["P0", "P1", "P2", "P3", "P4", "P5"]}
                requiredHighlight={!normalizePrioridade(form.prioridade)}
              />
              <FieldEdit
                label="Responsável pelo Aceite"
                value={form.responsavel_pelo_aceite}
                onChange={setF("responsavel_pelo_aceite")}
                requiredHighlight={!trim(form.responsavel_pelo_aceite)}
              />
            </div>
          </section>
        </div>

        <div className={s.actions}>
          <button
            className={`${s.btn} ${s.ghost}`}
            type="button"
            onClick={close}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            className={`${s.btn} ${s.primary}`}
            type="button"
            onClick={criar}
            disabled={saving}
          >
            {saving ? "Criando..." : "Criar Programação"}
          </button>
        </div>
      </div>
      <W2DateErrorModal
        open={!!dateError}
        message={dateError}
        onClose={() => setDateError("")}
      />
    </div>
  );
}

/* ---- fields ---- */
function FieldEdit({
  label,
  value,
  onChange,
  placeholder = "",
  className = "",
  requiredHighlight = false,
  optionalHighlight = false,
  type = "text",
  step,
  disabled = false,
}) {
  return (
    <label className={`${s.field} ${className || ""}`}>
      <span className={s.label}>{cleanFieldLabel(label)}</span>
      <input
        type={type}
        step={step}
        disabled={disabled}
        readOnly={disabled}
        className={`${s.input} ${requiredHighlight ? s.required : ""} ${
          optionalHighlight ? s.optional : ""
        }`}
        value={value ?? ""}
        onChange={(e) => onChange?.(e)}
        placeholder={placeholder}
      />
    </label>
  );
}

function FieldSelect({
  label,
  value,
  onChange,
  options = [],
  placeholder = "Selecione...",
  searchable = false,
  searchPlaceholder = "Digite para filtrar...",
  className = "",
  requiredHighlight = false,
  optionalHighlight = false,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const safeOpts = useMemo(
    () =>
      (Array.isArray(options) ? options : [])
        .map((opt) =>
          typeof opt === "object"
            ? {
                value: String(opt?.value ?? opt?.label ?? "").trim(),
                label: String(opt?.label ?? opt?.value ?? "").trim(),
              }
            : {
                value: String(opt ?? "").trim(),
                label: String(opt ?? "").trim(),
              }
        )
        .filter((opt) => opt.value),
    [options]
  );

  const selectedOption = useMemo(
    () => safeOpts.find((opt) => opt.value === String(value ?? "")) || null,
    [safeOpts, value]
  );

  const filteredOpts = useMemo(() => {
    const foldedQuery = foldSearch(query);
    if (!foldedQuery) return safeOpts;
    return safeOpts.filter(
      (opt) =>
        foldSearch(opt.label).includes(foldedQuery) ||
        foldSearch(opt.value).includes(foldedQuery)
    );
  }, [query, safeOpts]);

  useEffect(() => {
    if (searchable) {
      setQuery(selectedOption?.label || "");
      return;
    }
    setQuery("");
  }, [searchable, selectedOption]);

  useEffect(() => {
    if (!searchable || open) return;
    setQuery(selectedOption?.label || "");
  }, [open, searchable, selectedOption]);

  useEffect(() => {
    if (!open || !searchable) return undefined;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open, searchable]);

  useEffect(() => {
    if (!open) return undefined;

    const onOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) closeMenu();
    };
    const onEsc = (event) => {
      if (event.key === "Escape") closeMenu();
    };

    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setOpenUp(false);
      return undefined;
    }

    const updateDirection = () => {
      const rootEl = rootRef.current;
      if (!rootEl) return;
      const rect = rootEl.getBoundingClientRect();
      const scrollBoundary =
        rootEl.closest(`.${s.content}`) || rootEl.closest(`.${s.modal}`);
      const boundaryRect = scrollBoundary?.getBoundingClientRect?.() || null;
      const itemCount = Math.max(filteredOpts.length, 1);
      const menuHeight = Math.min(Math.max(itemCount * 40, 56), 220) + 8;
      const spaceBelow = boundaryRect
        ? boundaryRect.bottom - rect.bottom
        : window.innerHeight - rect.bottom;
      const spaceAbove = boundaryRect ? rect.top - boundaryRect.top : rect.top;
      setOpenUp(spaceBelow < menuHeight && spaceAbove > spaceBelow);
    };

    updateDirection();
    window.addEventListener("resize", updateDirection);
    window.addEventListener("scroll", updateDirection, true);
    return () => {
      window.removeEventListener("resize", updateDirection);
      window.removeEventListener("scroll", updateDirection, true);
    };
  }, [filteredOpts.length, open, searchable]);

  const triggerClass = `${s.input} ${requiredHighlight ? s.required : ""} ${
    optionalHighlight ? s.optional : ""
  } ${s.selectTrigger}`.trim();

  const closeMenu = () => {
    const activeEl = typeof document !== "undefined" ? document.activeElement : null;
    if (activeEl && rootRef.current?.contains(activeEl) && typeof activeEl.blur === "function") {
      activeEl.blur();
    }
    setOpen(false);
  };

  return (
    <label className={`${s.field} ${className || ""}`}>
      <span className={s.label}>{cleanFieldLabel(label)}</span>
      <div
        ref={rootRef}
        className={`${s.selectRoot} ${open ? s.selectOpen : ""} ${
          openUp ? s.selectOpenUp : ""
        } ${disabled ? s.selectDisabled : ""}`.trim()}
      >
        {searchable ? (
          open ? (
            <div
              className={`${triggerClass} ${s.selectTriggerSearch} ${s.selectTriggerEditing}`.trim()}
              role="combobox"
              aria-haspopup="listbox"
              aria-expanded={open}
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) {
                  e.preventDefault();
                  closeMenu();
                  inputRef.current?.blur();
                }
              }}
            >
              <input
                ref={inputRef}
                type="text"
                value={query}
                disabled={disabled}
                className={`${s.selectInlineInput} ${
                  selectedOption || query ? "" : s.selectPlaceholder
                }`.trim()}
                placeholder={searchPlaceholder || placeholder}
                onMouseDown={(e) => {
                  if (document.activeElement === inputRef.current) {
                    e.preventDefault();
                    closeMenu();
                    inputRef.current?.blur();
                  }
                }}
                onChange={(e) => {
                  if (disabled) return;
                  setQuery(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (disabled) return;
                  if (e.key === "Escape") {
                    setQuery(selectedOption?.label || "");
                    closeMenu();
                    inputRef.current?.blur();
                  }
                }}
              />
            </div>
          ) : (
            <button
              type="button"
              className={triggerClass}
              onClick={() => {
                if (!disabled) setOpen(true);
              }}
              disabled={disabled}
              aria-haspopup="listbox"
              aria-expanded={open}
            >
              <span
                className={`${s.selectValue} ${
                  selectedOption ? "" : s.selectPlaceholder
                }`.trim()}
              >
                {selectedOption?.label || searchPlaceholder || placeholder}
              </span>
            </button>
          )
        ) : (
          <button
            type="button"
            className={triggerClass}
            onClick={() => {
              if (!disabled) setOpen((prev) => !prev);
            }}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <span
              className={`${s.selectValue} ${
                selectedOption ? "" : s.selectPlaceholder
              }`.trim()}
            >
              {selectedOption?.label || placeholder}
            </span>
          </button>
        )}

        <div
          className={`${s.selectMenu} ${openUp ? s.selectMenuUp : ""}`.trim()}
          role="listbox"
        >
          {filteredOpts.map((opt) => {
            const selected = selectedOption?.value === opt.value;
            return (
              <button
                type="button"
                key={opt.value}
                className={`${s.selectOption} ${
                  selected ? s.selectOptionSelected : ""
                }`.trim()}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setQuery(opt.label);
                  onChange?.({ target: { value: opt.value } });
                  closeMenu();
                }}
                aria-selected={selected}
              >
                {opt.label}
              </button>
            );
          })}

          {!filteredOpts.length && (
            <div className={s.selectEmpty}>Nenhuma opção encontrada.</div>
          )}
        </div>
      </div>
    </label>
  );
}

function DateFieldSelect({
  label,
  value,
  onChange,
  className = "",
  requiredHighlight = false,
  optionalHighlight = false,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const rootRef = useRef(null);

  const selectedDate = useMemo(
    () => parseYMDToUtcDate(value) || parseYMDToUtcDate(getTodayYMD()),
    [value]
  );
  const [viewMonth, setViewMonth] = useState(() => startOfUtcMonth(selectedDate));

  useEffect(() => {
    if (!open) setViewMonth(startOfUtcMonth(selectedDate));
  }, [open, selectedDate]);

  const updateDirection = () => {
    const rootEl = rootRef.current;
    if (!rootEl) return;
    const rect = rootEl.getBoundingClientRect();
    const scrollBoundary =
      rootEl.closest(`.${s.content}`) || rootEl.closest(`.${s.modal}`);
    const boundaryRect = scrollBoundary?.getBoundingClientRect?.() || null;
    const menuHeight = 330;
    const spaceBelow = boundaryRect
      ? boundaryRect.bottom - rect.bottom
      : window.innerHeight - rect.bottom;
    const spaceAbove = boundaryRect ? rect.top - boundaryRect.top : rect.top;
    setOpenUp(spaceBelow < menuHeight && spaceAbove > spaceBelow);
  };

  useEffect(() => {
    if (!open) return undefined;

    const onOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) closeMenu();
    };
    const onEsc = (event) => {
      if (event.key === "Escape") closeMenu();
    };

    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setOpenUp(false);
      return undefined;
    }

    updateDirection();
    window.addEventListener("resize", updateDirection);
    window.addEventListener("scroll", updateDirection, true);
    return () => {
      window.removeEventListener("resize", updateDirection);
      window.removeEventListener("scroll", updateDirection, true);
    };
  }, [open, viewMonth]);

  const triggerClass = `${s.input} ${requiredHighlight ? s.required : ""} ${
    optionalHighlight ? s.optional : ""
  } ${s.selectTrigger}`.trim();

  const closeMenu = () => {
    const activeEl = typeof document !== "undefined" ? document.activeElement : null;
    if (activeEl && rootRef.current?.contains(activeEl) && typeof activeEl.blur === "function") {
      activeEl.blur();
    }
    setOpen(false);
  };

  const monthStart = startOfUtcMonth(viewMonth);
  const gridStart = addUtcDays(monthStart, -monthStart.getUTCDay());
  const calendarDays = Array.from({ length: 42 }, (_, index) => addUtcDays(gridStart, index));
  const todayDate = parseYMDToUtcDate(getTodayYMD());

  return (
    <label className={`${s.field} ${className || ""}`}>
      <span className={s.label}>{label}</span>
      <div
        ref={rootRef}
        className={`${s.selectRoot} ${s.dateRoot} ${open ? s.selectOpen : ""} ${
          openUp ? s.selectOpenUp : ""
        } ${disabled ? s.selectDisabled : ""}`.trim()}
      >
        <button
          type="button"
          className={`${triggerClass} ${s.dateTrigger}`.trim()}
          onClick={() => {
            if (disabled) return;
            if (!open) updateDirection();
            setOpen((prev) => !prev);
          }}
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <span className={s.selectValue}>{formatUtcDateToBR(selectedDate)}</span>
        </button>

        <div
          className={`${s.selectMenu} ${s.dateMenu} ${openUp ? s.selectMenuUp : ""}`.trim()}
          role="dialog"
        >
          <div className={s.dateHeader}>
            <button
              type="button"
              className={s.dateNav}
              onClick={() => setViewMonth((prev) => addUtcMonths(prev, -1))}
              aria-label="Mes anterior"
            >
              {"<"}
            </button>
            <div className={s.dateTitle}>
              {viewMonth.toLocaleDateString("pt-BR", {
                month: "long",
                year: "numeric",
                timeZone: "UTC",
              })}
            </div>
            <button
              type="button"
              className={s.dateNav}
              onClick={() => setViewMonth((prev) => addUtcMonths(prev, 1))}
              aria-label="Proximo mes"
            >
              {">"}
            </button>
          </div>

          <div className={s.dateWeekdays}>
            {NOVA_DATE_WEEKDAYS.map((weekday, index) => (
              <span key={`${weekday}-${index}`} className={s.dateWeekday}>
                {weekday}
              </span>
            ))}
          </div>

          <div className={s.dateGrid}>
            {calendarDays.map((day) => {
              const isOutside = day.getUTCMonth() !== viewMonth.getUTCMonth();
              const isSelected = isSameUtcDay(day, selectedDate);
              const isToday = isSameUtcDay(day, todayDate);

              return (
                <button
                  type="button"
                  key={formatUtcDateToYMD(day)}
                  className={`${s.dateDay} ${isOutside ? s.dateDayOutside : ""} ${
                    isSelected ? s.dateDaySelected : ""
                  } ${isToday ? s.dateDayToday : ""}`.trim()}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onChange?.({ target: { value: formatUtcDateToYMD(day) } });
                    closeMenu();
                  }}
                  aria-pressed={isSelected}
                >
                  {day.getUTCDate()}
                </button>
              );
            })}
          </div>

          <div className={s.dateFooter}>
            <button
              type="button"
              className={s.dateAction}
              onClick={() => {
                onChange?.({ target: { value: getTodayYMD() } });
                closeMenu();
              }}
            >
              Hoje
            </button>
            <button
              type="button"
              className={`${s.dateAction} ${s.dateActionGhost}`.trim()}
              onClick={closeMenu}
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </label>
  );
}
